import { useEffect, useMemo, useCallback } from 'react';

/**
 * Hook for keyboard shortcuts in Tickets Management
 *
 * @param {Object} options - Configuration options
 * @param {Function} options.onCreateNew - Handler for creating new ticket (C key)
 * @param {Function} options.onSearch - Handler for focusing search (/ or Ctrl+K)
 * @param {Function} options.onRefresh - Handler for refreshing list (R key)
 * @param {Function} options.onEscape - Handler for escape key (close dialogs, go back)
 * @param {Function} options.onShowShortcuts - Handler for showing shortcuts dialog (? key)
 * @param {Function} options.onOpenFilters - Handler for opening filters (F key)
 * @param {Function} options.onEdit - Handler for editing current ticket (E key)
 * @param {Function} options.onDelete - Handler for deleting current ticket (D key)
 * @param {Function} options.onExportPdf - Handler for exporting to PDF (P key)
 * @param {Function} options.onNextTicket - Handler for navigating to next ticket (J or Arrow Down)
 * @param {Function} options.onPrevTicket - Handler for navigating to previous ticket (K or Arrow Up)
 * @param {Function} options.onSelectTicket - Handler for selecting/opening ticket (Enter)
 * @param {boolean} enabled - Whether shortcuts are enabled
 */
export const useKeyboardShortcuts = (options = {}, enabled = true) => {
  const {
    onCreateNew,
    onSearch,
    onRefresh,
    onEscape,
    onShowShortcuts,
    onOpenFilters,
    onEdit,
    onDelete,
    onExportPdf,
    onNextTicket,
    onPrevTicket,
    onSelectTicket
  } = options;

  const handleKeyDown = useCallback((e) => {
    // Get the active element
    const activeElement = document.activeElement;
    const isInInput =
      activeElement?.tagName === 'INPUT' ||
      activeElement?.tagName === 'TEXTAREA' ||
      activeElement?.isContentEditable;

    const key = e.key;
    const ctrl = e.ctrlKey || e.metaKey;
    const shift = e.shiftKey;

    // Escape always works, even in inputs
    if (key === 'Escape') {
      if (onEscape) {
        e.preventDefault();
        onEscape(e);
      }
      return;
    }

    // Don't process other shortcuts when typing in inputs
    if (isInInput) {
      // Exception: Ctrl+K for search
      if (ctrl && key.toLowerCase() === 'k' && onSearch) {
        e.preventDefault();
        onSearch(e);
      }
      return;
    }

    // Single key shortcuts (only when not in input)
    switch (key.toLowerCase()) {
      case 'c':
        if (!ctrl && !shift && onCreateNew) {
          e.preventDefault();
          onCreateNew(e);
        }
        break;

      case '/':
        if (!ctrl && !shift && onSearch) {
          e.preventDefault();
          onSearch(e);
        }
        break;

      case 'r':
        if (!ctrl && !shift && onRefresh) {
          e.preventDefault();
          onRefresh(e);
        }
        break;

      case 'f':
        if (!ctrl && !shift && onOpenFilters) {
          e.preventDefault();
          onOpenFilters(e);
        }
        break;

      case 'e':
        if (!ctrl && !shift && onEdit) {
          e.preventDefault();
          onEdit(e);
        }
        break;

      case 'd':
        if (!ctrl && !shift && onDelete) {
          e.preventDefault();
          onDelete(e);
        }
        break;

      case 'p':
        if (!ctrl && !shift && onExportPdf) {
          e.preventDefault();
          onExportPdf(e);
        }
        break;

      case '?':
        if (shift && onShowShortcuts) {
          e.preventDefault();
          onShowShortcuts(e);
        }
        break;

      case 'j':
      case 'arrowdown':
        if (!ctrl && !shift && onNextTicket) {
          e.preventDefault();
          onNextTicket(e);
        }
        break;

      case 'k':
      case 'arrowup':
        if (!ctrl && !shift && onPrevTicket) {
          e.preventDefault();
          onPrevTicket(e);
        }
        break;

      case 'enter':
        if (!ctrl && !shift && onSelectTicket) {
          e.preventDefault();
          onSelectTicket(e);
        }
        break;

      default:
        break;
    }

    // Ctrl+K for search
    if (ctrl && key.toLowerCase() === 'k' && onSearch) {
      e.preventDefault();
      onSearch(e);
    }
  }, [
    onCreateNew,
    onSearch,
    onRefresh,
    onEscape,
    onShowShortcuts,
    onOpenFilters,
    onEdit,
    onDelete,
    onExportPdf,
    onNextTicket,
    onPrevTicket,
    onSelectTicket
  ]);

  useEffect(() => {
    if (!enabled) return;

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [enabled, handleKeyDown]);
};

/**
 * Hook to get keyboard shortcuts help data
 */
export const useShortcutsHelp = () => {
  const shortcuts = useMemo(() => ({
    'Navigation': [
      { keys: ['/', 'Ctrl+K'], description: 'Focus search' },
      { keys: ['J', '↓'], description: 'Next ticket' },
      { keys: ['K', '↑'], description: 'Previous ticket' },
      { keys: ['Enter'], description: 'Open selected ticket' },
      { keys: ['Esc'], description: 'Close / Go back' }
    ],
    'Actions': [
      { keys: ['C'], description: 'Create new ticket' },
      { keys: ['R'], description: 'Refresh list' },
      { keys: ['F'], description: 'Open filters' },
      { keys: ['P'], description: 'Export to PDF' },
      { keys: ['?'], description: 'Show this help' }
    ],
    'Ticket Detail': [
      { keys: ['E'], description: 'Edit ticket' },
      { keys: ['Esc'], description: 'Go back to list' }
    ]
  }), []);

  return shortcuts;
};

export default useKeyboardShortcuts;
